// SPDX-License-Identifier: MIT
/**
 * color-flip (turn, 3–5 beats; reviewed in the Kestrel reel). The film's colour flips: a panel in
 * the brand colour slams up over the frame and one short line pops in glyph by glyph in whichever
 * of ink or paper reads best on it. At the end the panel splits open to the ground colour. Use it
 * once per film, for the line the film turns on.
 */
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import * as kit from '../kit/index.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { fitLine, hasCJK, sceneBeats } from './lib.ts';
/** One line, or two balanced lines when it is long (split at the space or comma nearest the middle). */
export function splitLine(line: string): string[] {
    const chars = Array.from(line);
    if (chars.length <= (hasCJK(line) ? 9 : 14))
        return [line];
    const mid = chars.length / 2;
    let best = -1;
    chars.forEach((c, i) => {
        if (!/[\s,，、。:：]/.test(c))
            return;
        if (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))
            best = i;
    });
    if (best <= 0 || best >= chars.length - 1)
        return [line];
    const keep = /\s/.test(chars[best]!) ? best : best + 1;
    return [
        chars.slice(0, keep).join('').trim(),
        chars
            .slice(best + 1)
            .join('')
            .trim(),
    ].filter(Boolean);
}
export default defineScene({
    id: 'color-flip',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema: z.object({ line: z.string().check(z.maxLength(24)), kicker: z.optional(z.string().check(z.maxLength(40))) }),
    glyphs: (p) => p.line + (p.kicker ?? ''),
    motion: (_p, env) => (env.localBeat < 0.8 || env.localBeat > sceneBeats(env) - 0.7 ? 1 : 0.2),
    post: (_p, env) => ({
        bloom: 0.25,
        grain: 1.3,
        vignette: 0.6,
        contrast: 1.05,
        shake: 0.9 * kit.beat.hits(env, [0.12], 8),
        aberration: 0.2 * kit.beat.hits(env, [0.12], 8),
    }),
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const L = sceneBeats(env);
        const { colors } = env.brand;
        const panel = th.primary;
        const ink = kit.color.readableOn(panel, [colors.ink, colors.paper]);
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        const rise = kit.beat.seg(env, 0, 0.16, kit.curves.glide);
        const split = kit.beat.seg(env, L - 0.65, L, kit.curves.snap);
        const panelTop = DESIGN_H * (1 - rise);
        const lines = splitLine(props.line);
        const px = Math.min(...lines.map((l) => fitLine(ctx, env.brand, l, 900, lines.length > 1 ? 260 : 300, 1560)));
        const lh = px * 1.05;
        const tracking = (l: string) => (hasCJK(l) ? '0px' : `${-px / 90}px`);
        const type = (c: CanvasRenderingContext2D) => {
            c.save();
            kit.camera.zoom(c, 1 + 0.05 * kit.beat.seg(env, 0.4, L - 0.6, kit.curves.silk));
            c.textBaseline = 'alphabetic';
            c.font = font(env.brand, 900, px);
            c.fillStyle = ink;
            const total = lines.reduce((n, l) => n + Array.from(l).length, 0);
            let offset = 0;
            lines.forEach((l, li) => {
                c.letterSpacing = tracking(l);
                const first = offset;
                kit.text.glyphs(c, l, DESIGN_W / 2, DESIGN_H / 2 + px / 3 + (li - (lines.length - 1) / 2) * lh, 'center', (i) => {
                    const at = (first + i) / total;
                    const k = kit.beat.seg(env, 0.15 + at * 0.45, 0.45 + at * 0.45, kit.curves.overshoot);
                    return { alpha: Math.min(1, k * 3), scale: 1.25 - 0.25 * k, dy: 30 * (1 - k) };
                });
                offset += Array.from(l).length;
            });
            if (props.kicker) {
                c.letterSpacing = '6px';
                c.textAlign = 'center';
                c.font = font(env.brand, 600, 28, 'mono');
                c.fillStyle = rgba(ink, 0.65 * kit.beat.seg(env, 0.8, 1.2));
                c.fillText(props.kicker, DESIGN_W / 2, DESIGN_H / 2 - px * 0.9 - ((lines.length - 1) * lh) / 2);
            }
            c.restore();
            c.letterSpacing = '0px';
        };
        const panelOnly = (c: CanvasRenderingContext2D) => {
            c.fillStyle = panel;
            c.fillRect(0, panelTop, DESIGN_W, DESIGN_H - panelTop);
            if (rise < 1) {
                c.fillStyle = rgba(th.dark ? '#FFFFFF' : colors.paper, 0.9);
                c.fillRect(0, panelTop - 3, DESIGN_W, 6);
            }
        };
        const content = (c: CanvasRenderingContext2D) => {
            panelOnly(c);
            type(c);
        };
        if (split <= 0) {
            // Resting: the line goes into the crisp layer so the lens effects never soften it.
            panelOnly(ctx);
            env.crisp(type);
        }
        else {
            const off = split * (DESIGN_H / 2 + 40);
            for (const [y0, dir] of [
                [0, -1],
                [DESIGN_H / 2, 1],
            ] as const) {
                ctx.save();
                ctx.beginPath();
                ctx.rect(0, y0 + dir * off, DESIGN_W, DESIGN_H / 2);
                ctx.clip();
                ctx.translate(0, dir * off);
                content(ctx);
                ctx.restore();
            }
            ctx.fillStyle = rgba(th.dark ? '#FFF1E6' : panel, 0.8 * (1 - split));
            ctx.fillRect(0, DESIGN_H / 2 - off - 2, DESIGN_W, 4);
            ctx.fillRect(0, DESIGN_H / 2 + off - 2, DESIGN_W, 4);
        }
    },
});
